import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addMonths, firstDayOfMonth, todayIso, toIsoDate } from "@/lib/format";
import { cn } from "@/lib/utils";

interface CalendarProps {
  /** Currently selected ISO date (YYYY-MM-DD) or empty. */
  value: string;
  onSelect: (iso: string) => void;
  /** When true, days before today are disabled. */
  blockPast?: boolean;
  /** When true, days after today are disabled. */
  blockFuture?: boolean;
  className?: string;
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/** Single-month calendar grid. Used inside DateField popovers. */
export function Calendar({ value, onSelect, blockPast, blockFuture, className }: CalendarProps) {
  const initial = useMemo(() => {
    if (value) {
      const [y, m] = value.split("-").map(Number);
      return new Date(y!, (m ?? 1) - 1, 1);
    }
    return firstDayOfMonth(new Date());
  }, [value]);
  const [cursor, setCursor] = useState<Date>(initial);

  const grid = useMemo(() => buildGrid(cursor), [cursor]);
  const today = todayIso();

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-popover p-3 shadow-2xl text-popover-foreground",
        className,
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCursor(addMonths(cursor, -1))}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground/80 hover:bg-foreground/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <strong className="text-sm">
          {cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}
        </strong>
        <button
          type="button"
          onClick={() => setCursor(addMonths(cursor, 1))}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground/80 hover:bg-foreground/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[0.65rem] font-bold uppercase tracking-wider text-muted-foreground">
        {WEEKDAYS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {grid.map((cell, idx) => {
          if (!cell) return <span key={idx} className="h-10" />;
          const iso = cell;
          const isToday = iso === today;
          const isSelected = iso === value;
          const isPast = blockPast && iso < today;
          const isFuture = blockFuture && iso > today;
          const disabled = isPast || isFuture;
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(iso)}
              className={cn(
                // h-10 keeps day cells at a ~40px touch target (44px
                // with the grid gap) — h-8 was a frequent mis-tap on
                // tablets.
                "flex h-10 items-center justify-center rounded-md text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                disabled
                  ? "cursor-not-allowed text-muted-foreground/40"
                  : "hover:bg-primary/15 hover:text-foreground",
                isToday && !isSelected && "border border-primary text-primary",
                isSelected &&
                  "bg-gradient-to-br from-primary/30 to-[hsl(var(--accent-2)/0.25)] text-foreground font-bold",
              )}
            >
              {Number(iso.slice(8))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function buildGrid(cursor: Date): Array<string | null> {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const firstWeekday = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells: Array<string | null> = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(toIsoDate(new Date(y, m, d)));
  }
  return cells;
}
